import { connect } from 'react-redux';
import { countBy, intersection, keyBy } from 'lodash';
import i18next from 'i18next';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button, Dropdown, ListView } from '@commons/utils/styledLibraryComponents';
import { ConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CONFIRM_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { STANDARD_LISTVIEW_PADDING } from '@commons/constants/listViewProps';
import EmptyState from '@commons/EmptyState';
import mixpanelUtils, { ENUM_EVENT_SOURCES, ENUM_EVENTS } from '@commons/utils/mixpanel';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import { canCreateInventoryListTemplate } from '@selectors/actions/inventoryListTemplateActions';
import { getAuthorizedActionsForPaths } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

import { storeSupplierProductMapping as storeSupplierProductMappingService } from '@services/storeSupplierProductMapping';
import clientService from '@services/client';
import inventoryListTemplateService from '@services/inventoryListTemplate';

import InventoryListTemplateProviderContext from '@context/InventoryListTemplateContext';

import { Container, HeaderContainer, ListViewContainer } from './styledComponents';
import actionsUtils from './utils/actionsUtils';
import columnsUtils from './utils/columnsUtils';
import exportUtils from './utils/exportUtils';
import modalsUtils, {
  ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS,
} from './utils/modalsUtils';

/**
 * The count to display depends on whether the template's store is a central kitchen, and the store
 * list is loaded asynchronously by the app shell. Deriving it at render time instead of at fetch
 * time keeps the count correct even when the store list lands after the templates.
 */
const getAssociatedRecipesCount = (entities, isKitchenStore) => {
  const mappedRecipesCounts = countBy(entities, (recipe) => {
    if (recipe.isKitchen) {
      return 'kitchenRecipesCount';
    }

    return 'salesPointRecipesCount';
  });

  if (isKitchenStore) {
    return mappedRecipesCounts.kitchenRecipesCount ?? 0;
  }

  return mappedRecipesCounts.salesPointRecipesCount ?? 0;
};

const InventoryListTemplates = (props) => {
  const {
    match: { path },
    client: { clientId, storeName, hasLocalCatalogs, hasMultipleBrands },
    showErrorMessage,
    showSuccessMessage,
    history,
    pageLoading,
    pageLoaded,
    openGenericModal,
    refreshGenericModal,
    authorizedActions,
    stores,
    modal,
  } = props;

  const clientStoreName = getClientStoreNameTranslation(storeName, false);

  const { selectedStores, setSelectedStores } = useContext(InventoryListTemplateProviderContext);

  const hasInitializedSelectedStores = useRef(selectedStores.length > 0);

  const [isLoading, setIsLoading] = useState(true);

  const [inventoryListTemplates, setInventoryListTemplates] = useState([]);
  const [selectedILTs, setSelectedILTs] = useState([]);

  const [supplierProductsToAdd, setSupplierProductsToAdd] = useState([]);
  const [spAvailableForAssociation, setSPAvailableForAssociation] = useState([]);
  const [recipesToAdd, setRecipesToAdd] = useState([]);
  const [inventoryListTemplateToCopy, setInventoryListTemplateToCopy] = useState(null);
  const [currentStep, setCurrentStep] = useState(
    ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS,
  );

  const columns = useMemo(() => columnsUtils.getListColumns(clientStoreName), [clientStoreName]);

  const storesKeyById = useMemo(() => keyBy(stores, 'id'), [stores]);

  const inventoryListTemplatesWithRecipesCount = useMemo(
    () =>
      inventoryListTemplates.map((inventoryListTemplate) => {
        const { entities, storeId } = inventoryListTemplate;

        return {
          ...inventoryListTemplate,
          associatedRecipesCount: getAssociatedRecipesCount(
            entities,
            storesKeyById[storeId]?.isKitchen,
          ),
        };
      }),
    [inventoryListTemplates, storesKeyById],
  );

  const filteredInventoryListTemplates = useMemo(() => {
    const selectedStoreIdsSet = new Set(selectedStores.map(({ id }) => id));

    return inventoryListTemplatesWithRecipesCount.filter(({ storeId }) =>
      selectedStoreIdsSet.has(storeId),
    );
  }, [selectedStores, inventoryListTemplatesWithRecipesCount]);

  const displayEmptyState = !isLoading && !inventoryListTemplates.length;

  const selectedILTsStoreIds = useMemo(
    () => selectedILTs.map(({ storeId }) => storeId).filter((storeId) => !!storeId),
    [selectedILTs],
  );

  const alreadyAssociatedEntityIds = useMemo(
    () => intersection(...selectedILTs.map(({ entityIds }) => entityIds)),
    [selectedILTs],
  );

  useEffect(() => {
    if (!stores.length || hasInitializedSelectedStores.current) {
      return;
    }

    hasInitializedSelectedStores.current = true;
    setSelectedStores(stores);
  }, [stores]);

  useEffect(() => {
    (async () => {
      await fetchInventoryTemplates();
    })();
  }, []);

  useEffect(() => {
    if (!inventoryListTemplateToCopy) {
      return;
    }

    handleCopyParamModal(true);
  }, [inventoryListTemplateToCopy]);

  useEffect(() => {
    if (!selectedILTsStoreIds.length || !modal?.GenericModalBool) {
      return;
    }

    openAddReferencesModal(
      selectedILTsStoreIds,
      spAvailableForAssociation,
      alreadyAssociatedEntityIds,
      true,
    );
  }, [currentStep, recipesToAdd]);

  const fetchInventoryTemplates = async () => {
    pageLoading();
    setIsLoading(true);

    try {
      const clientILTs = await clientService.getInventoryListTemplates({
        withMappings: true,
        filterByUserCatalog: true,
      });

      const formattedILTs = clientILTs.map((mapping) => {
        const supplierProducts = mapping.supplierProducts ?? [];

        return {
          ...mapping,
          supplierProductIds: supplierProducts.map(({ id }) => id),
          entityIds: (mapping.entities ?? []).map(({ id }) => id),
          associatedSPsCount: supplierProducts.length,
        };
      });

      setInventoryListTemplates(formattedILTs);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_INVENTORY_LIST_TEMPLATES_ERROR'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const deleteInventoryListTemplates = async (templatesToDelete) => {
    pageLoading();
    setIsLoading(true);

    try {
      const storageAreaIds = templatesToDelete.map(({ id }) => id);
      await clientService.deleteInventoryListTemplates(clientId, storageAreaIds);

      await fetchInventoryTemplates();
      showSuccessMessage(
        i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.DELETION_SUCCESS', {
          count: storageAreaIds.length,
        }),
      );
    } catch {
      showErrorMessage(i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.DELETION_ERROR'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const handleDeletion = (selectedItems) => {
    const count = selectedItems.length;

    const params = {
      type: 'warning',
      width: '542px',
      height: 'auto',
      icon: '/images/inpulse/warning-white-small.svg',
      title: i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.LABEL_DELETE_ACTION', {
        count,
      }),
      component: ConfirmationModal,
      data: {
        content: i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.DELETE_CONFIRMATION_MESSAGE', {
          count,
          name: selectedItems[0]?.name ?? '',
        }),
      },
      actions: [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          ...GENERIC_MODAL_CONFIRM_BUTTON(),
          handleClick: async () => await deleteInventoryListTemplates(selectedItems),
        },
      ],
    };

    openGenericModal(params);
  };

  const handleExport = () => {
    const inventoryListTemplatesToExport = selectedILTs.length
      ? selectedILTs
      : filteredInventoryListTemplates;

    exportUtils.exportInventoryListTemplates(inventoryListTemplatesToExport, clientStoreName);
  };

  const resetCopyModal = () => {
    setInventoryListTemplateToCopy(null);
  };

  const handleCopyParamModal = async (shouldRefreshModal = false) => {
    if (!selectedILTs.length) {
      return;
    }

    const selectedILTIds = new Set(selectedILTs.map(({ id }) => id));

    const params = modalsUtils.getCopyParamModalConfig({
      handleParamsCopyConfirmation,
      inventoryListTemplateToCopy,
      availableInventoryListTemplates: inventoryListTemplates.filter(
        ({ id }) => !selectedILTIds.has(id),
      ),
      setInventoryListTemplateToCopy,
      stores,
      resetCopyModal,
    });

    if (shouldRefreshModal === true) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const handleParamsCopyConfirmation = () => {
    if (!inventoryListTemplateToCopy) {
      return;
    }

    const params = modalsUtils.getCopyParamsConfirmationModalConfig({
      handleParamsCopy,
      inventoryListTemplateToCopy,
      selectedILTsCount: selectedILTs.length,
    });

    openGenericModal(params);
  };

  const handleParamsCopy = async () => {
    if (!inventoryListTemplateToCopy) {
      return;
    }

    pageLoading();
    setIsLoading(true);

    try {
      const targetInventoryListTemplateIds = selectedILTs.map(({ id }) => id);

      await inventoryListTemplateService.copyInventoryListTemplateParams(
        inventoryListTemplateToCopy.id,
        targetInventoryListTemplateIds,
      );

      mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_INVENTORY_TEMPLATE, {
        source: ENUM_EVENT_SOURCES.CLONE,
        inventoryListTemplateIds: targetInventoryListTemplateIds,
      });

      resetCopyModal();

      showSuccessMessage(
        i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.COPY_INVENTORY_LIST_TEMPLATE_PARAMS_SUCCESS'),
      );

      await fetchInventoryTemplates();
    } catch {
      showErrorMessage(
        i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.COPY_INVENTORY_LIST_TEMPLATE_PARAMS_ERROR'),
      );
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const getSPAvailableForAssociation = async (storeIds, supplierProductsToIgnore = []) => {
    pageLoading();

    try {
      const supplierProducts = await storeSupplierProductMappingService.getByStoreIds(
        storeIds,
        supplierProductsToIgnore,
      );

      return supplierProducts;
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_ERROR'));

      return [];
    } finally {
      pageLoaded();
    }
  };

  const handleAddReferences = async () => {
    if (!selectedILTsStoreIds.length) {
      return;
    }

    const alreadyAssociatedSupplierProductIds = intersection(
      ...selectedILTs.map(({ supplierProductIds }) => supplierProductIds),
    );

    const supplierProducts = await getSPAvailableForAssociation(
      selectedILTsStoreIds,
      alreadyAssociatedSupplierProductIds,
    );

    setSPAvailableForAssociation(supplierProducts);

    openAddReferencesModal(selectedILTsStoreIds, supplierProducts, alreadyAssociatedEntityIds);
  };

  const openAddReferencesModal = (
    storeIds,
    supplierProducts,
    associatedEntityIds,
    shouldRefreshModal = false,
  ) => {
    const params = modalsUtils.getAddReferenceStepModalConfig({
      currentStep,
      setCurrentStep,
      hasLocalCatalogs,
      setSpToBeAdded: setSupplierProductsToAdd,
      availableSPForAssociation: supplierProducts,
      alreadySelectedSupplierProducts: supplierProductsToAdd,
      clientId,
      hasMultipleBrands,
      setRecipesToBeAdded: setRecipesToAdd,
      storeIds,
      alreadyMappedRecipes: associatedEntityIds.map((id) => ({ id })),
      handleAddingReferences: () => {
        addInventoryListTemplateMappings(
          selectedILTs.map(({ id }) => id),
          supplierProductsToAdd.map(({ id }) => id),
          recipesToAdd.map(({ entityId }) => entityId),
        );
      },
      resetModal: () => {
        setSupplierProductsToAdd([]);
        setRecipesToAdd([]);
      },
    });

    if (shouldRefreshModal) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const addInventoryListTemplateMappings = async (
    inventoryListTemplateIds,
    supplierProductIds,
    entityIds,
  ) => {
    pageLoading();
    setIsLoading(true);

    try {
      await inventoryListTemplateService.addInventoryListTemplateMappings(
        inventoryListTemplateIds,
        supplierProductIds,
        entityIds,
      );

      mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_INVENTORY_TEMPLATE, {
        source: ENUM_EVENT_SOURCES.LIST_SELECTION,
        inventoryListTemplateIds,
      });

      showSuccessMessage(i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.ADD_REFERENCES_SUCCESS'));

      await fetchInventoryTemplates();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.ADD_REFERENCES_ERROR'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const goToCreateILT = () => {
    history.push('/admin/inventories/inventory-list-templates/create');
  };

  const goToILTDetailsPage = (inventoryListTemplateId) => {
    history.push(
      `/admin/inventories/inventory-list-templates/${inventoryListTemplateId.id}/details`,
    );
  };

  const actions = useMemo(
    () =>
      actionsUtils.getGlobalActions({
        authorizedActions,
        goToCreateILT,
        selectedILTs,
        handleCopyParamModal,
        handleDeletion,
        handleExport,
        handleAddReferences,
      }),
    [filteredInventoryListTemplates, selectedILTs, authorizedActions],
  );

  const rowActions = useMemo(
    () => actionsUtils.getRowActions({ handleDeletion, authorizedActions }),
    [authorizedActions],
  );

  const renderNoILTs = () => (
    <EmptyState
      label={i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.EMPTY_STATE_TITLE')}
      labelColor={ENUM_COLORS.IP_BLACK_1}
      labelFont={ENUM_FONTS.H2_INTER}
      renderActionButton={() =>
        canCreateInventoryListTemplate(authorizedActions) && (
          <Button
            color={'inpulse-default'}
            handleClick={goToCreateILT}
            icon={'/images/inpulse/add-white-small.svg'}
            label={i18next.t('GENERAL.CREATE')}
          />
        )
      }
      subtitle={i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.EMPTY_STATE_SUBTITLE')}
      subtitleMargin={'8px'}
    />
  );

  return (
    <ListViewContainer>
      <NavigationBreadCrumb featurePath={path} />
      <Container>
        {displayEmptyState ? (
          renderNoILTs()
        ) : (
          <ListView
            actionOnClick={goToILTDetailsPage}
            actions={!isLoading && actions}
            columns={columns}
            data={filteredInventoryListTemplates}
            isLoading={isLoading}
            padding={STANDARD_LISTVIEW_PADDING}
            placeholderShape={i18next.t('GENERAL.SEARCH')}
            renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
            renderFilterButton={() => (
              <HeaderContainer>
                <Dropdown
                  iconSrc={'/images/inpulse/store-black-small.svg'}
                  isDisabled={isLoading}
                  isUniqueSelection={false}
                  items={stores}
                  selectedItems={selectedStores}
                  isRequired
                  onSelectionChange={setSelectedStores}
                />
              </HeaderContainer>
            )}
            rowActions={rowActions}
            setSelectedItems={setSelectedILTs}
            hideAllPerPageOption
          />
        )}
      </Container>
    </ListViewContainer>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  stores: state.baseReducer.activeStores,
  modal: state.modalReducer,
  authorizedActions: getAuthorizedActionsForPaths(state.baseReducer.userRights, [
    '/admin/inventories/inventory-list-templates',
    '/admin/inventories/inventory-list-templates/create',
    '/admin/inventories/inventory-list-templates/:id/details',
  ]),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(InventoryListTemplates);
